import { useCallback, useMemo } from 'react';
import { useApolloClient } from '@apollo/client';

import {
  TaskSmartLinksDocument,
  TaskSmartLinksQuery,
  TaskSmartLinksQueryVariables,
  useTaskSmartLinksQuery,
} from 'core/graphql';

import { useDebouncedValue } from '../Meta/useDebouncedValue';

/** Подсказка связи по тексту заголовка (ТЗ 0109). `id` — id ОБЪЕКТА. */
export interface ITaskSmartLink {
  kind: string;
  id: string;
  label: string;
  hint: string;
  /** student_id | student_name | group_number | student_deal | student_agreement */
  reason: string;
  /** `false` — токен совпал с несколькими объектами: не заводим сами. */
  confident: boolean;
}

export const SMART_LINKS_DEBOUNCE_MS = 400;
/** Короче — не спрашиваем: «П» совпадёт со всем подряд. */
const MIN_CHARS = 2;

/**
 * Подсказки связей по набранному заголовку (ТЗ 0109, `taskSmartLinks`).
 *
 * Пауза 400 мс — запрос на слово, а не на букву. `no-cache`: ответ — функция
 * текста, а не сущность; в кеше Apollo он копился бы по строке на каждое
 * промежуточное состояние заголовка. Права и виды, закрытые роли, отсекает
 * сервер — клиент показывает ровно то, что пришло.
 */
export interface IUseTaskSmartLinks {
  links: ITaskSmartLink[];
  loading: boolean;
  /** Подсказки уже посчитаны для ЭТОГО текста (пауза прошла, ответ пришёл). */
  settled: boolean;
  /** Спросить сейчас, мимо паузы — перед отправкой формы. */
  fetchNow: (text: string) => Promise<ITaskSmartLink[]>;
}

export const useTaskSmartLinks = (
  text: string,
  enabled = true,
): IUseTaskSmartLinks => {
  const client = useApolloClient();
  const debounced = useDebouncedValue(text.trim(), SMART_LINKS_DEBOUNCE_MS);
  const skip = !enabled || debounced.length < MIN_CHARS;
  const { data, loading } = useTaskSmartLinksQuery({
    variables: { text: debounced },
    skip,
    fetchPolicy: 'no-cache',
  });
  const links = useMemo(
    () => (skip ? [] : ((data?.taskSmartLinks || []) as ITaskSmartLink[])),
    [data, skip],
  );
  const fetchNow = useCallback(
    async (raw: string): Promise<ITaskSmartLink[]> => {
      const value = raw.trim();
      if (value.length < MIN_CHARS) return [];
      const res = await client.query<
        TaskSmartLinksQuery,
        TaskSmartLinksQueryVariables
      >({
        query: TaskSmartLinksDocument,
        variables: { text: value },
        fetchPolicy: 'no-cache',
      });
      return (res.data?.taskSmartLinks || []) as ITaskSmartLink[];
    },
    [client],
  );

  const settled =
    !enabled ||
    text.trim().length < MIN_CHARS ||
    (debounced === text.trim() && !loading);
  return { links, loading: !skip && loading, settled, fetchNow };
};
